import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";

import { useParams } from 'react-router-dom';

import { Divider, IconButton, InputAdornment } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { changePassword } from "../../../../../services/account/change-password";
import { ChangePasswordRequestBody } from "../../../../../services/account/change-password/types";
import { StyledButton } from "../../../../../components/Button/button";
import { Input } from "../../../../../components/Input/input";
import styled from "styled-components";
import { theme } from "../../../../../helpers/theme";
import SessionService from "../../../../../services/session-service";
import { SuccessMessage } from "../../../../operator-views/operator-subcontractors-detail-edite/components/forms-wraper/helpers/success-message";
import { successMessageContent } from "../success-message/success-message-text";
import { SuccessMessageSettings } from "../success-message/success-message";

export const PasswordChange = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isChanged, setIsChanged] = useState(false);
    const [openSuccessInfo, setOpenSuccessInfo] = useState(false);

    useEffect(() => {
        setIsChanged(false);
        setOpenSuccessInfo(false);
    }, []);

    useEffect(() => {
    }, [isChanged]);

    const passwordChangeMutation = useMutation(changePassword, {
        onSuccess: (res) => {
            setIsChanged(false);
            setOpenSuccessInfo(true);
            formik.resetForm();
        },
    });


    const validationSchema = yup.object().shape({
        haslo: yup.string()
            .min(8, 'Za którkie hasło')
            .max(50, 'Za długie hasło')
            .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$@!%&*?&/<>.:;“'])[A-Za-z\d#$@!%&*?&/<>.:;“']{8,50}$/, 'Zaproponowane hasło nie spełnia założonych kryteriów.')
            .required("Pole wymagane"),

        confirm_password: yup.string()
            .when("haslo", {
                is: (val: any) => (val && val.length > 0 ? true : false),
                then: yup.string().oneOf(
                    [yup.ref("haslo")],
                    "Niezgodność haseł"
                ),
            })
            .required("Pole wymagane"),
    });
    const handleIsEmptyInput = (haslo: string, confirmation: string): void => {
        if (formik.values.confirm_password !== '' && formik.values.haslo !== "") {
            return setIsChanged(true);
        }
        return setIsChanged(false);
    }
    const formik = useFormik({
        initialValues: {
            haslo: "",
            confirm_password: "",
        },
        validationSchema: validationSchema,

        onSubmit: (values: ChangePasswordRequestBody) => {
            const body =
            {
                haslo: btoa(values.haslo),
            }
            handleSubmitPasswordChange(body);
        },

    });

    const handleSubmitPasswordChange = (body: ChangePasswordRequestBody) => {
        passwordChangeMutation.mutate(body);
    };


    return (

        <form
            onSubmit={formik.handleSubmit}
            onChange={() => handleIsEmptyInput(formik.values.haslo, formik.values.confirm_password)}
        >

            <FormWrapper>
                <Description>{`Nowe hasło powinno posiadać od 8 do 50 znaków, w  tym  co najmniej: 1 - dużą literę, 1 - małą literę, 1 - cyfrę, 1 - znak specjalny.`}
                </Description>
                <Input
                    label="Nowe hasło"
                    id="haslo"
                    name="haslo"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.haslo}
                    onChange={formik.handleChange}
                    error={formik.touched.haslo && Boolean(formik.errors.haslo)}
                    helperText={formik.touched.haslo && formik.errors.haslo}
                    onBlur={formik.handleBlur}
                    InputProps={{
                        disableUnderline: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle haslo visibility"
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    variant="filled"
                />
                <Input
                    label="Powtórz nowe hasło"
                    id="confirm_password"
                    name="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formik.values.confirm_password}
                    onChange={formik.handleChange}
                    error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
                    helperText={formik.touched.confirm_password && formik.errors.confirm_password}
                    onBlur={formik.handleBlur}
                    InputProps={{
                        disableUnderline: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle confirm_password visibility"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    edge="end"
                                >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    variant="filled"
                />
                {!openSuccessInfo ?

                    <ColumnsWrapper>
                        <ButtonWrapper>
                            <StyledButton
                                type="submit"
                                variant="contained"
                                sx={{
                                    height: "30px",
                                }}
                                disabled={
                                    Boolean(formik.errors.haslo)
                                    || Boolean(formik.errors.confirm_password)
                                    || !isChanged
                                }
                            >
                                Zapisz
                            </StyledButton >
                        </ButtonWrapper>

                    </ColumnsWrapper>
                    :
                    < SuccessMessageSettings
                        message={successMessageContent[0]}
                        setOpenSuccessInfo={setOpenSuccessInfo}
                        openSuccessInfo={openSuccessInfo}
                        setIsChanged={setIsChanged}
                    />}
            </FormWrapper>
        </form >

    );


}

const FormWrapper = styled.div`
padding-top:8px;
display: flex;
flex-direction: column;
gap: 12px;
`;
const Description = styled.div`
font-family: ${theme.fonts.interRegular};
font-style: normal;
font-weight: ${theme.fontWeight.small};
font-size: 14px;
line-height: 20px;
color: ${theme.colors.black_primaryText};
margin-top:0px;
padding:0px 8px;
margin-bottom:8px;
`;
const ColumnsWrapper = styled.div`
display:flex;
flex-direction:row;
justify-content: flex-end;
`;
const ButtonWrapper = styled.div`

`;
